import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { dayCheckinStatusColor } from '../../utils/dayCheckinStatus';

const MARK_ROLES = ['teacher', 'teacher_supervisor', 'teacher_admin'];

function expectedLine(row) {
  if (!row) return '';
  if (row.expectedStartLabel) return `Expected start ${row.expectedStartLabel}`;
  return 'No work hours set for today';
}

export default function TeacherDayCheckinCard() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const allowed = hasRole(MARK_ROLES);
  const [promptOpen, setPromptOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const query = useQuery(
    ['teacherDayCheckinToday'],
    async () => {
      const res = await teacherModuleAPI.getDayCheckinToday();
      return res.data?.data;
    },
    { enabled: allowed, refetchOnWindowFocus: true }
  );

  const markMutation = useMutation(() => teacherModuleAPI.markDayCheckin(), {
    onSuccess: (res) => {
      if (res.data?.data) queryClient.setQueryData(['teacherDayCheckinToday'], res.data.data);
      queryClient.invalidateQueries(['teacherDayCheckinMine']);
      setPromptOpen(false);
      toast.success(res.data?.alreadyMarked ? 'Already marked for today' : 'Marked present');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Could not mark present');
    },
  });

  const row = query.data;
  const needsMark = Boolean(row?.canMark);

  useEffect(() => {
    if (needsMark && !dismissed) setPromptOpen(true);
    if (!needsMark) setPromptOpen(false);
  }, [needsMark, dismissed]);

  const dismissPrompt = () => {
    setDismissed(true);
    setPromptOpen(false);
  };

  if (!allowed) return null;
  if (query.isLoading) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <CircularProgress size={22} />
      </Paper>
    );
  }
  if (query.error?.response?.status === 404) return null;
  if (query.isError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {query.error?.response?.data?.message || 'Could not load today\'s start'}
      </Alert>
    );
  }

  if (!row) return null;

  const renderMarkButton = () => (
    <Button
      variant="contained"
      size="large"
      onClick={() => markMutation.mutate()}
      disabled={markMutation.isLoading}
      sx={{
        minWidth: { sm: 220 },
        py: 1.5,
        px: 3,
        fontWeight: 700,
        fontSize: '1.05rem',
      }}
    >
      {markMutation.isLoading ? 'Marking…' : 'Mark present'}
    </Button>
  );

  return (
    <>
      <Paper
        sx={{
          p: 2.5,
          mb: 2,
          ...(needsMark
            ? {
                bgcolor: '#e3f2fd',
                border: '2px solid',
                borderColor: 'primary.main',
                boxShadow: 3,
              }
            : {}),
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={needsMark ? 800 : 600}>
              {needsMark ? 'Mark yourself present' : "Today's start"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {row.workDateLabel}
              {' · '}
              {expectedLine(row)}
            </Typography>
            {needsMark ? (
              <Typography variant="body1" sx={{ mt: 1, fontWeight: 600 }}>
                Confirm you have started work today. This stays here until you mark present.
              </Typography>
            ) : null}
            {row.onLeave ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                You are on approved leave today.
              </Typography>
            ) : null}
            {row.markedAtLabel ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="body1">Marked at {row.markedAtLabel}</Typography>
                <Chip size="small" label={row.statusLabel} color={dayCheckinStatusColor(row.status)} />
              </Stack>
            ) : null}
            {!row.expectedStartLabel && !row.onLeave ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Without work hours, the punch is saved and is not compared to a start time.
              </Typography>
            ) : null}
          </Box>
          {needsMark ? renderMarkButton() : null}
        </Stack>
      </Paper>

      <Dialog open={promptOpen} onClose={dismissPrompt} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.35rem' }}>Mark yourself present</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {row.workDateLabel}
            {' · '}
            {expectedLine(row)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mark present before you start classes. This opens each time you come to the dashboard until you do.
          </Typography>
        </DialogContent>
        <DialogActions
          disableSpacing
          sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
        >
          {renderMarkButton()}
          <Button onClick={dismissPrompt} disabled={markMutation.isLoading}>
            Not now
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
